import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import useLang from '../../i18n/useLang';
import Button from './Button';
import logo from '../../images/logotrasnparent.png';

export const navItems = [
  { to: '/aboutGrovv', key: 'header.About' },
  { to: '/services', key: 'header.Services' },
  { to: '/Competence', key: 'header.Competence' },
  { to: '/Customers', key: 'header.Costumers' },
  { to: '/Blog', key: 'header.Blog' },
];

function LanguageSwitch({ className = '' }) {
  const { t, isHe, setLang } = useLang();
  return (
    <button
      type="button"
      onClick={() => setLang(isHe ? 'en' : 'he')}
      lang={isHe ? 'en' : 'he'}
      className={`inline-flex min-h-[44px] items-center rounded-lg px-3 text-[0.95rem] font-medium text-ink-600 transition-colors hover:bg-fog-100 hover:text-ink ${className}`}
    >
      {t('site.switchLabel')}
    </button>
  );
}

export default function Nav() {
  const { t } = useLang();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const linkClass = ({ isActive }) =>
    `relative rounded-md px-3 py-2 text-[0.95rem] font-medium transition-colors after:absolute after:inset-x-3 after:-bottom-0.5 after:h-0.5 after:origin-[0%_50%] after:rounded-full after:bg-grovv after:transition-transform after:duration-300 rtl:after:origin-[100%_50%] ${
      isActive ? 'text-ink after:scale-x-100' : 'text-ink-600 after:scale-x-0 hover:text-ink hover:after:scale-x-100'
    }`;

  return (
    <header
      className={`sticky top-0 z-40 border-b transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled || open ? 'border-fog-200 bg-white/90 shadow-[0_8px_24px_-20px_rgba(15,34,54,.5)] backdrop-blur-md' : 'border-transparent bg-white'
      }`}
    >
      <div className="wrap flex min-h-[72px] items-center justify-between gap-6">
        <Link to="/" aria-label={t('site.homeAria')} className="flex shrink-0 items-center rounded-lg">
          <img src={logo} alt="Grovv" width="92" height="36" className="h-9 w-auto" />
        </Link>

        <nav aria-label="Main" className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass}>
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitch className="hidden sm:inline-flex" />
          <Button to="/contact" className="hidden sm:inline-flex">
            {t('header.Contact')}
          </Button>
          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-ink ring-1 ring-inset ring-fog-300 transition-colors hover:bg-fog-100 lg:hidden"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? t('site.closeMenu') : t('site.openMenu')}
            onClick={() => setOpen((v) => !v)}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              {open ? (
                <>
                  <path d="M6 6l12 12" />
                  <path d="M18 6L6 18" />
                </>
              ) : (
                <>
                  <path d="M4 7h16" />
                  <path d="M4 12h16" />
                  <path d="M4 17h16" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div
          id="mobile-menu"
          className="border-t border-fog-200 bg-white lg:hidden"
          onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        >
          <nav aria-label="Mobile" className="wrap flex flex-col py-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex min-h-[52px] items-center border-b border-fog-100 text-lg font-medium ${isActive ? 'text-grovv-800' : 'text-ink'}`
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
            <div className="flex flex-wrap items-center gap-3 pt-5">
              <Button to="/contact" size="lg" arrow className="flex-1">
                {t('header.Contact')}
              </Button>
              <LanguageSwitch />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
